/**
 * Get the current session token. Checks the window global first (set by AuthGate),
 * falls back to localStorage (always available). This avoids the race condition
 * where components mount before AuthGate's useEffect sets the window variable.
 */
export function getSessionToken(): string | null {
  return (window as any).__mfxSessionToken || localStorage.getItem("mfx_session");
}

/**
 * Get the current authenticated user object.
 *
 * If impersonation is active, returns the TARGET user (with role,
 * permissions, accountId etc swapped) so nav, route gates, and any
 * downstream consumer behaves as if logged in as the target. The
 * original admin's identity is still in `getRealUser()`.
 */
export function getCurrentUser(): any | null {
  // Impersonation overlay — direct localStorage read avoids circular
  // imports (session ↔ store ↔ persist).
  const imp = _readImpersonationState();
  if (imp?.target) {
    return {
      ...imp.target,
      _impersonatedBy: getRealUser(),
    };
  }
  return getRealUser();
}

/** Internal: read + age-check the impersonation state from localStorage.
 * Returns null if no session, parse error, or session has expired. */
function _readImpersonationState(): {
  target: any;
  startedAt: string;
  maxDurationSeconds: number;
  sessionId: string | null;
} | null {
  try {
    const raw = localStorage.getItem("millbank-impersonation");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const s = parsed?.state;
    if (!s?.target?.id || !s?.startedAt) return null;
    const ageMs = Date.now() - new Date(s.startedAt).getTime();
    const maxMs = (s.maxDurationSeconds || 3600) * 1000;
    if (ageMs >= maxMs) return null;
    return s;
  } catch {
    return null;
  }
}

/**
 * Get the REAL session user, ignoring any active impersonation. Used
 * by the impersonation banner to show "Viewing as X · as Frazer".
 */
export function getRealUser(): any | null {
  const windowUser = (window as any).__mfxUser;
  if (windowUser) return windowUser;
  try {
    const stored = localStorage.getItem("mfx_user");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Build auth headers for API calls.
 */
export function authHeaders(): Record<string, string> {
  const token = getSessionToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * True if a "View as" impersonation is currently active. Use to hide
 * destructive UI (write buttons, delete actions) so admins don't try
 * mutations the backend will reject anyway.
 */
export function isImpersonating(): boolean {
  return _readImpersonationState() !== null;
}
